/**
 * Demo equity-curve generator for the banker AUM card.
 *
 * The backend (`/api/banker/me/aum`) only exposes a single snapshot per banker:
 * `totalEur`, `clientsCount`, and a YTD performance percentage. There is no
 * time series, and no performance figure for any other window. To render the
 * AUM card with a chip-selectable period (1M / 3M / 6M / YTD / 1Y / 3Y / MAX),
 * we synthesise:
 *
 *   - a stable per-period anchor % (deterministic, seeded by advisor code so
 *     the same banker always sees the same demo numbers across sessions);
 *   - a Brownian-bridge cumulative-return curve that lands exactly on the
 *     anchor at the right edge of the window, so the chart endpoint matches
 *     the headline percentage.
 *
 * The real `ytdPct` coming from the backend is preferred over the mocked YTD
 * anchor whenever it is available, so YTD is always honest. Every other
 * period is mocked. This whole module is consumed under a `<DemoDataChip>`
 * flag and should be deleted once the backend exposes a real banker AUM
 * history (one cumulative-return point per trading day, with multi-period
 * summaries alongside).
 */
export type BankerPerformanceRange = '1M' | '3M' | '6M' | 'YTD' | '1Y' | '3Y' | 'MAX'

export const BANKER_PERFORMANCE_RANGES: ReadonlyArray<BankerPerformanceRange> = [
  '1M',
  '3M',
  '6M',
  'YTD',
  '1Y',
  '3Y',
  'MAX',
]

export const BANKER_PERFORMANCE_DEFAULT_RANGE: BankerPerformanceRange = '1Y'

export interface BankerPerformancePoint {
  /** ISO timestamp for the point — used as a stable x key. */
  iso: string
  /** Localised label rendered in the tooltip header. */
  tooltipLabel: string
  /** Cumulative % return from the start of the window. */
  value: number
}

/** Number of points per range, picked to read smoothly without crowding. */
const POINT_COUNT: Record<BankerPerformanceRange, number> = {
  '1M': 22,
  '3M': 14,
  '6M': 26,
  YTD: 26,
  '1Y': 13,
  '3Y': 37,
  MAX: 61,
}

/** Step length in days for each range. */
const STEP_DAYS: Record<BankerPerformanceRange, number> = {
  '1M': 1.4,
  '3M': 7,
  '6M': 7,
  YTD: 10,
  '1Y': 30.4,
  '3Y': 30.4,
  MAX: 30.4,
}

/**
 * Demo anchor % per period. Tuned to look like a healthy private-banking
 * portfolio: moderate single-digit returns over short windows, double-digit
 * cumulative over multi-year windows, with realistic noise per banker.
 */
const ANCHOR_BASE: Record<BankerPerformanceRange, number> = {
  '1M': 0.6,
  '3M': 1.8,
  '6M': 3.4,
  YTD: 4.2,
  '1Y': 6.8,
  '3Y': 14.5,
  MAX: 28.0,
}

const fullDateFormatter = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
})

function hashSeed(input: string): number {
  let hash = 0
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash |= 0
  }
  return hash >>> 0
}

function mulberry32(seed: number): () => number {
  let state = seed || 1
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date.getTime())
  next.setDate(next.getDate() + Math.round(days))
  return next
}

function parseLatestEndDt(raw: string | null | undefined): Date {
  if (raw) {
    const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(raw)
    if (compact) {
      return new Date(Number(compact[1]), Number(compact[2]) - 1, Number(compact[3]))
    }
    const parsed = new Date(raw)
    if (!Number.isNaN(parsed.getTime())) return parsed
  }
  return new Date()
}

/**
 * Resolve the headline % for a given range. Real `ytdPct` overrides the
 * mocked YTD anchor when present; every other range is computed from the
 * base anchor with a small banker-specific jitter so two bankers never see
 * identical numbers.
 */
export function resolveBankerAnchorPct(
  range: BankerPerformanceRange,
  seedKey: string,
  realYtdPct: number | null | undefined,
): number {
  if (range === 'YTD' && typeof realYtdPct === 'number' && Number.isFinite(realYtdPct)) {
    return realYtdPct
  }
  const base = ANCHOR_BASE[range]
  // Per-banker jitter: deterministic, in the ±25% range around the base.
  const jitterRand = mulberry32(hashSeed(`${seedKey}|anchor|${range}`))()
  const jitter = (jitterRand - 0.5) * 0.5 * Math.abs(base || 1)
  return base + jitter
}

/**
 * Build a deterministic cumulative-return series for the given window.
 *
 *   - First point sits at 0% on the left edge of the window.
 *   - Last point sits exactly at `anchorPct` so the chart endpoint matches
 *     the displayed headline.
 *   - Intermediate points follow a Brownian-bridge-like walk so the curve
 *     reads as a believable equity line rather than a straight diagonal.
 */
export function buildBankerPerformanceSeries(
  seedKey: string,
  range: BankerPerformanceRange,
  anchorPct: number,
  latestEndDt: string | null | undefined,
): BankerPerformancePoint[] {
  const n = POINT_COUNT[range]
  const stepDays = STEP_DAYS[range]
  const endDate = parseLatestEndDt(latestEndDt)
  const rand = mulberry32(hashSeed(`${seedKey}|series|${range}`))

  const volatility = Math.max(Math.abs(anchorPct) * 0.35, 0.6)

  const raw: number[] = [0]
  for (let i = 1; i < n; i += 1) {
    raw.push(raw[i - 1] + (rand() - 0.5) * volatility)
  }
  const rawEnd = raw[n - 1]

  const points: BankerPerformancePoint[] = []
  for (let i = 0; i < n; i += 1) {
    const t = i / (n - 1)
    const detrended = raw[i] - t * rawEnd
    const value = detrended + t * anchorPct
    const offsetSteps = n - 1 - i
    const date = addDays(endDate, -offsetSteps * stepDays)
    points.push({
      iso: date.toISOString(),
      tooltipLabel: fullDateFormatter.format(date),
      value,
    })
  }
  return points
}
